import { QueryFormatterFactory } from '~/modules/queryFormatters/QueryFormatterFactory'
import type { QueryParams } from '~/modules/queryFormatters/DefaultQueryFormatter'
import pick from 'lodash/pick'

export default function useFilterBar({
  componentType,
  filterBar,
}: {
  componentType?: Ref<string>
  filterBar?: Ref<HTMLElement>
} = {}) {
  const route = useRoute()
  const router = useRouter()
  const filters = ref<QueryParams>({})
  const sortBy = ref('name:asc')
  const isFirstLoad = ref(true)
  const { $scrollTo } = useNuxtApp()

  const filterBarModel = computed({
    get() {
      return {
        filters: filters.value,
        sort: sort.value,
      }
    },
    set(value) {
      setSortBy(value.sort.by)
    },
  })

  const queryFormatter = computed(() => {
    if (!componentType.value)
      throw new Error('Filter Bar `contentType` not set!')
    return QueryFormatterFactory(componentType.value)
  })
  const defaultFilters = computed(() =>
    queryFormatter.value.getDefaultFilters(),
  )
  const defaultSortBy = computed(() => queryFormatter.value.getDefaultSort())
  const sortOptions = computed(() => queryFormatter.value.getSortOptions())
  const sort = computed(() => ({
    by: sortBy.value,
    options: sortOptions.value,
  }))

  const queryParams = computed(() => {
    const params: Record<string, string> = {}
    // get filters
    const filterValues = unref(filters)
    const keys = Object.keys(filterValues)
    keys.forEach((key) => {
      const filter = filterValues[key]
      if (filter.length) params[kebabCase(key)] = filters.value[key].join(',')
    })
    // get sort
    // if sort = default, we'll leave it out of the params
    // this avoids an ugly reload applying them when hitting the page without any params
    if (sort.value.by !== defaultSortBy.value) params.sort_by = sort.value.by
    // done
    return params
  })

  function resetFilters() {
    filters.value = { ...defaultFilters.value }
  }

  function setupFilters() {
    const query = route.query
    Object.keys(query).forEach((key) => {
      if (query[key] && validFilters.includes(key)) {
        filters.value[key] = (query[key] as string).split(',')
      }
    })
  }

  // ---- Sort Related Methods
  function resetSort() {
    sortBy.value = '' + defaultSortBy.value
  }

  function setupSort() {
    const routeSortBy = route.query?.sort_by
    setSortBy(routeSortBy)
  }

  function setSortBy(value) {
    if (
      value &&
      sortBy.value !== value &&
      sortOptions.value.filter((option) => option.value === value)
    ) {
      sortBy.value = value
    }
  }

  function scrollTop() {
    $scrollTo(filterBar.value, 400, {
      easing: 'ease-in-out',
      lazy: true,
      force: true,
    })
  }

  watch(queryParams, (newValue, oldValue) => {
    if (newValue !== oldValue) {
      router.push({
        query: {
          // keep any acceptable extra params
          ...pick(route.query, extraParams),
          // overwrite with our filter changes
          ...queryParams.value,
        },
      })
      // scroll to top, but not on first load of view
      if (!isFirstLoad.value) {
        scrollTop()
      }
    }
  })

  onBeforeMount(() => {
    resetSort()
    resetFilters()
    setupSort()
    setupFilters()
  })

  onMounted(() => {
    nextTick(() => {
      isFirstLoad.value = false
    })
  })

  return {
    filterBarModel,
    resetFilters,
    setSortBy,
  }
}
